import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Button, Chip, Grid, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import { translateLabel } from 'services/Utilities/labelUtils';
import { getStatusConfig } from 'enums/CertificationStatusEnum';
import { useProductLines } from 'services/Data/ProductLineServices';
import { useCriteriaOptions } from 'services/Data/CriteriaOptionServices';
import {
  getActiveProductOfferings,
  getProductLineColor,
  getProductLineIcon,
  sortProductLinesByDisplayOrder,
} from 'services/Utilities/productOfferingsConfig';

// Submission header aligned with Certification Tool (title, status, score, dealer info, product offerings)
const SubmissionHeader = ({ submission }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const history = useHistory();

  const { data: productLines } = useProductLines();
  const { data: criteriaOptions } = useCriteriaOptions();

  const dealer = submission?.dealer;

  const statusConfig = useMemo(() => (submission ? getStatusConfig(submission.status) : null), [submission]);
  const StatusIcon = statusConfig?.icon;

  const heroUnitProductLineId = useMemo(() => {
    if (!submission) return null;
    return submission.heroUnitProductLine_Id || submission.heroUnitProductLine?.id || null;
  }, [submission]);

  const filteredProductLines = useMemo(() => getActiveProductOfferings(dealer, productLines || []), [dealer, productLines]);

  const orderedProductLines = useMemo(() => sortProductLinesByDisplayOrder(filteredProductLines || []), [
    filteredProductLines,
  ]);

  const approvedOptionName = useMemo(() => {
    if (!submission) {
      return null;
    }

    if (submission.approvedCriteriaOption) {
      return translateLabel(submission.approvedCriteriaOption.name, i18n.language);
    }

    if (!submission.approvedCriteriaOption_Id || !criteriaOptions) {
      return null;
    }

    const match = criteriaOptions.find(option => option.id === submission.approvedCriteriaOption_Id);
    if (!match) {
      return null;
    }

    return translateLabel(match.name, i18n.language);
  }, [criteriaOptions, i18n.language, submission]);

  const showScore = submission?.score !== null && submission?.score !== undefined && submission?.score !== 0;
  const localizedName = useMemo(() => {
    if (!submission?.criteria?.nameLabel) {
      return null;
    }

    const translated = translateLabel(submission.criteria.nameLabel, i18n.language);
    return translated || submission.name || null;
  }, [submission, i18n.language]);

  if (!submission) return null;

  return (
    <Box sx={{ mb: { xs: 3, md: 4 } }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => history.push('/dealer/certification/submissions')}
        sx={{
          color: theme.palette.primary.main,
          mb: { xs: 1.5, md: 2 },
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.1),
          },
          mt: 3,
        }}>
        <Typography variant="h3">{t('common.back')}</Typography>
      </Button>

      <Paper
        data-tour="submissionInfo"
        elevation={8}
        sx={{
          bgcolor: isDarkMode ? theme.palette.grey[900] : theme.palette.background.paper,
          borderRadius: 2,
          p: { xs: 2, md: 3 },
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          display: 'flex',
          flexDirection: 'column',
        }}>
        <Grid container spacing={isMobile ? 3 : 4} alignItems="stretch">
          <Grid item xs={12} md={7}>
            <Box sx={{ mb: { xs: 2, md: 3 }, textAlign: isMobile ? 'center' : 'left' }}>
              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                sx={{
                  color: theme.palette.primary.main,
                  mb: 1.5,
                  textTransform: 'uppercase',
                  lineHeight: 1.2,
                }}>
                {localizedName || submission.name || t('submissions.untitled')}{' '}
                {submission?.dateIssued ? `${new Date(submission.dateIssued).toLocaleDateString()} ` : ''}-{' '}
                {submission.dealer_Id}
              </Typography>
            </Box>

            {dealer ? (
              <Box data-tour="dealerInfo" sx={{ textAlign: isMobile ? 'center' : 'left' }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: 'center',
                    gap: isMobile ? 1 : 2,
                    mb: 2,
                    ml: isMobile ? 0 : 4.5,
                  }}>
                  <Typography
                    variant="h3"
                    sx={{ color: isDarkMode ? theme.palette.common.white : theme.palette.text.primary, fontWeight: 700 }}>
                    {dealer.customerName || submission.dealerName} - {submission.dealer_Id}
                    {dealer.principalContact ? ` (${dealer.principalContact})` : ''}
                  </Typography>
                  <Chip
                    label={dealer.isBrpExclusive ? t('submission.brponly') : t('submission.multioem')}
                    size="small"
                    sx={{
                      bgcolor: dealer.isBrpExclusive ? theme.palette.primary.main : theme.palette.info.main,
                      color: dealer.isBrpExclusive ? theme.palette.common.black : theme.palette.common.white,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                    }}
                  />
                </Box>

                <Stack spacing={1.2} sx={{ ml: isMobile ? 0 : 4.5 }} alignItems={isMobile ? 'center' : 'flex-start'}>
                  {dealer.city || dealer.postalCode || dealer.region ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocationOnIcon
                        sx={{
                          fontSize: 18,
                          color: isDarkMode ? alpha(theme.palette.primary.main, 0.6) : theme.palette.text.secondary,
                          mr: 1.5,
                        }}
                      />
                      <Typography
                        variant="h3"
                        sx={{
                          color: isDarkMode ? alpha(theme.palette.common.white, 0.7) : theme.palette.text.secondary,
                          fontSize: '0.875rem',
                        }}>
                        {dealer.city}
                        {dealer.region?.internalName ? `, ${dealer.region.internalName}` : ''}
                        {dealer.postalCode ? `, ${dealer.postalCode}` : ''}
                      </Typography>
                    </Box>
                  ) : null}
                  {dealer.phoneNumber ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PhoneIcon
                        sx={{
                          fontSize: 18,
                          color: isDarkMode ? alpha(theme.palette.primary.main, 0.6) : theme.palette.text.secondary,
                          mr: 1.5,
                        }}
                      />
                      <Typography
                        variant="h3"
                        sx={{
                          color: isDarkMode ? alpha(theme.palette.common.white, 0.7) : theme.palette.text.secondary,
                          fontSize: '0.875rem',
                        }}>
                        {dealer.phoneNumber}
                      </Typography>
                    </Box>
                  ) : null}
                </Stack>
              </Box>
            ) : null}
          </Grid>

          <Grid item xs={12} md={5}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                height: '100%',
                justifyContent: 'space-between',
              }}>
              <Box sx={{ textAlign: isTablet ? 'center' : 'right', width: '100%' }}>
                {StatusIcon ? (
                  <Chip
                    data-tour="statusInfo"
                    icon={<StatusIcon />}
                    label={i18n.language === 'fr' ? statusConfig.labelFrench : statusConfig.labelEnglish}
                    sx={{
                      bgcolor: statusConfig.bgColor,
                      color: statusConfig.color,
                      borderColor: statusConfig.color,
                      border: '1px solid',
                      fontWeight: 700,
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      py: isMobile ? 1 : 2.5,
                      px: isMobile ? 1.5 : 2,
                      mb: isMobile ? 1.5 : 2,
                      '& .MuiChip-icon': {
                        color: statusConfig.color,
                      },
                    }}
                  />
                ) : null}

                {showScore ? (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'center', sm: 'center' }}
                    justifyContent={isTablet ? 'center' : 'flex-end'}
                    sx={{ mb: { xs: 2, md: 3 } }}>
                    <Typography
                      data-tour="scoreInfo"
                      variant={isMobile ? 'h3' : 'h2'}
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 900,
                        textAlign: isTablet ? 'center' : 'right',
                        lineHeight: 1.2,
                      }}>
                      {approvedOptionName ? `${approvedOptionName} - ` : ''}
                      {submission.score} {t('submission.points', { count: submission.score })}
                    </Typography>
                  </Stack>
                ) : null}
              </Box>

              {orderedProductLines.length > 0 ? (
                <Box
                  data-tour="productLineInfo"
                  sx={{
                    width: '100%',
                    bgcolor: alpha(theme.palette.primary.light, theme.palette.mode === 'dark' ? 0.12 : 0.15),

                    borderRadius: 2,
                    p: { xs: 2, md: 2.5 },
                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                    textAlign: isTablet ? 'center' : 'left',
                  }}>
                  <Typography
                    variant="h3"
                    sx={{
                      color: isDarkMode ? alpha(theme.palette.common.white, 0.5) : theme.palette.text.secondary,
                      mb: 1.5,
                      fontSize: isMobile ? '0.7rem' : '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontWeight: 700,
                    }}>
                    {t('certAccount.productOfferings')}
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: isTablet ? 'center' : 'flex-start' }}>
                    {orderedProductLines.map(productLine => {
                      const label =
                        translateLabel(productLine.name, i18n.language) ||
                        productLine.internalName ||
                        productLine.code ||
                        productLine.id;

                      const color = getProductLineColor(productLine.internalName);
                      const IconComponent = getProductLineIcon(productLine.internalName);
                      const isHeroSelection = heroUnitProductLineId && productLine.id === heroUnitProductLineId;

                      return (
                        <Chip
                          key={productLine.id}
                          icon={<IconComponent />}
                          label={label}
                          size="small"
                          sx={{
                            bgcolor: color,
                            color: theme.palette.common.white,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            py: 1.2,
                            borderRadius: 1.5,
                            opacity: heroUnitProductLineId ? (isHeroSelection ? 1 : 0.45) : 1,
                            filter: heroUnitProductLineId && !isHeroSelection ? 'grayscale(40%)' : 'none',
                            boxShadow: isHeroSelection ? `0 0 0 2px ${color}80, 0 10px 22px ${color}30` : 'none',
                            transform: isHeroSelection ? 'translateY(-2px)' : 'none',
                            transition: 'all 0.3s ease',
                            '& .MuiChip-icon': {
                              color: theme.palette.common.white,
                              fontSize: '1rem',
                              opacity: heroUnitProductLineId ? (isHeroSelection ? 1 : 0.55) : 1,
                            },
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              ) : null}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

SubmissionHeader.propTypes = {
  submission: PropTypes.object,
};

export default SubmissionHeader;
